import { useSyncExternalStore } from 'react';
import { isAxiosError } from 'axios';

import {
  billingRefusalFromProblem,
  type BillingRefusal,
} from '@/lib/billing-refusal';
import { TypedApiError } from '@/lib/typed-api-error';
import type { ErrorResponse } from '@/types/generated';

// The typed client parsed the problem body at construction; the axios client leaves it on the response.
export const billingRefusalOf = (error: unknown): BillingRefusal | null => {
  if (error instanceof TypedApiError) return error.billing;

  if (isAxiosError(error) && error.response) {
    const body = error.response.data as Partial<ErrorResponse> | undefined;
    return billingRefusalFromProblem(error.response.status, body);
  }

  return null;
};

// A refusal is the balance talking: a second request would be refused for the same reason.
export const retryUnlessRefused = (
  failureCount: number,
  error: Error,
): boolean => billingRefusalOf(error) === null && failureCount < 1;

type Listener = () => void;

let currentRefusal: BillingRefusal | null = null;
const listeners = new Set<Listener>();

const emit = (): void => {
  for (const listener of listeners) listener();
};

const subscribe = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = (): BillingRefusal | null => currentRefusal;

// A no-op for a non-billing error, so the mutation cache can forward every rejection blindly.
export const openBillingGate = (refusal: BillingRefusal | null): void => {
  if (!refusal) return;
  currentRefusal = refusal;
  emit();
};

export const closeBillingGate = (): void => {
  if (currentRefusal === null) return;
  currentRefusal = null;
  emit();
};

export const useBillingGate = (): BillingRefusal | null =>
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
